
import { useState, useEffect, useRef } from 'react';
import { FaCode, FaEdit, FaExclamationTriangle, FaEye, FaLanguage, FaSave, FaUndo } from "react-icons/fa";

const QuizRulesGenerator = ({ quizData, onDownload }) => {
    const [preview, setPreview] = useState(false);
    const [previewTab, setPreviewTab] = useState('visual');
    const [previewLanguage, setPreviewLanguage] = useState('english');
    const [editMode, setEditMode] = useState(false);
    const [htmlCode, setHtmlCode] = useState('');
    const [originalHtmlCode, setOriginalHtmlCode] = useState('');
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [formData, setFormData] = useState({
        quizTitle: "Champion's Arena",
        quizDescription: {
            english: "For true sports lovers! From cricket and football to Olympics – show off your sports smarts.",
            hindi: "सच्चे खेल प्रेमियों के लिए! क्रिकेट और फुटबॉल से लेकर ओलंपिक तक - अपनी खेल समझ दिखाएं।"
        },
        quizDuration: 24,
        timePerQuestion: 5,
        resultAnnouncement: 24,
        totalQuestions: 6,
        easyQuestions: 2,
        mediumQuestions: 2,
        hardQuestions: 2,
        totalCoinPool: 700,
        prizes: [
            { rank: "5th Place", coins: 500 },
            { rank: "2nd Place", coins: 200 }
        ],
        lifelinesAvailable: 2,
        playLimit: 2,
        quizType: "Daily",
        primaryColor: "#6849FF",
        secondaryColor: "#FFC800",
        scheduledDate: new Date().toISOString().split('T')[0],
        scheduledTime: "18:00",
        icons: "sport"
    });

    // Flag to track if we're using the edited HTML
    const [useEditedHtml, setUseEditedHtml] = useState(false);

    // Sync formData with quizData from parent component when available
    useEffect(() => {
        if (quizData) {
            // Map the quiz data from QuizAdd format to Rules component format
            const mappedData = {
                quizTitle: quizData.title || formData.quizTitle,
                quizDescription: {
                    english: quizData.description || formData.quizDescription.english,
                    hindi: quizData.description || formData.quizDescription.hindi, // Fallback for now
                },
                quizDuration: quizData.type === 'daily' ? 24 : Math.ceil(quizData.quizTiming / 60),
                timePerQuestion: quizData.questionTiming || formData.timePerQuestion,
                resultAnnouncement: quizData.type === 'daily' ? 24 : quizData.resultTiming || formData.resultAnnouncement,
                totalQuestions: quizData.totalQuestion || formData.totalQuestions,
                easyQuestions: quizData.questionDistribution?.easy || formData.easyQuestions,
                mediumQuestions: quizData.questionDistribution?.medium || formData.mediumQuestions,
                hardQuestions: quizData.questionDistribution?.hard || formData.hardQuestions,
                totalCoinPool: quizData.totalCoin || formData.totalCoinPool,
                prizes: quizData.type === 'live'
                    ? (quizData.winCoins?.map(wc => ({ rank: `${wc.rank}${getOrdinalSuffix(wc.rank)} Place`, coins: wc.coin })) || formData.prizes)
                    : (quizData.correctAnswerCoins?.map(cac => ({ rank: `${cac.correctAnswers} Correct Answers`, coins: cac.coin })) || formData.prizes),
                lifelinesAvailable: quizData.lifeLine || formData.lifelinesAvailable,
                playLimit: quizData.playLimit || formData.playLimit,
                quizType: quizData.type === 'daily' ? 'Daily' : 'Live',
                primaryColor: "#6849FF", // Default values since these aren't in QuizAdd
                secondaryColor: "#FFC800",
                scheduledDate: quizData.scheduleTime ? new Date(quizData.scheduleTime).toISOString().split('T')[0] : formData.scheduledDate,
                scheduledTime: quizData.scheduleTime ? new Date(quizData.scheduleTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : formData.scheduledTime,
                icons: quizData.type === 'daily' ? 'brain' : 'sport' // Example logic
            };

            setFormData(mappedData);
        }
    }, [quizData]);

    // Generate and set HTML when formData changes or when preview tab is set to 'code'
    useEffect(() => {
        if (previewTab === 'code' || !htmlCode) {
            const generatedHtml = generateHTML();
            setHtmlCode(generatedHtml);

            // Only set originalHtmlCode when we're generating fresh HTML
            if (!useEditedHtml) {
                setOriginalHtmlCode(generatedHtml);
            }
        }
    }, [formData, previewTab]);

    // Helper function to get ordinal suffix
    const getOrdinalSuffix = (num) => {
        const j = num % 10;
        const k = num % 100;

        if (j === 1 && k !== 11) {
            return "st";
        }
        if (j === 2 && k !== 12) {
            return "nd";
        }
        if (j === 3 && k !== 13) {
            return "rd";
        }
        return "th";
    };

    // Format date for display
    const formatDateForDisplay = (dateString, timeString, language = 'english') => {
        try {
            const dateObj = new Date(`${dateString}T${timeString}`);

            if (language === 'english') {
                const options = {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                };

                return dateObj.toLocaleDateString('en-US', options);
            } else {
                // Hindi date format (simplified)
                const hindiMonths = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
                const hindiDay = dateObj.getDate();
                const hindiMonth = hindiMonths[dateObj.getMonth()];
                const hindiYear = dateObj.getFullYear();
                const hindiHour = dateObj.getHours();
                const hindiMinute = dateObj.getMinutes();
                const hindiTime = `${hindiHour}:${hindiMinute < 10 ? '0' + hindiMinute : hindiMinute}`;
                return `${hindiDay} ${hindiMonth}, ${hindiYear} शाम ${hindiTime} बजे`;
            }
        } catch (e) {
            return language === 'english' ? 'Invalid date' : 'अमान्य तिथि';
        }
    };

    // Handle color change
    const handleColorChange = (colorType, value) => {
        setFormData({
            ...formData,
            [colorType]: value
        });

        // When colors change, we should use the newly generated HTML, not edited HTML
        setUseEditedHtml(false);
    };

    // Generate the HTML code - fixed to avoid CSP issues
    const generateHTML = () => {
        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${formData.quizTitle} - Quiz Rules</title>
    <style>
        :root {
            --primary-color: ${formData.primaryColor};
            --secondary-color: ${formData.secondaryColor};
            --accent-color: ${formData.secondaryColor};
            --light-bg: #F9FAFE;
            --dark-text: #111723;
            --light-text: #FFFFFF;
            --border-color: #CFD1D3;
            --subfont-color: #888B91;
            --delete-color: #F34235;
            --th-color: #F2F0FF;
            --th-hover-color: #F5F8FF;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        body {
            background-color: var(--light-bg);
            color: var(--dark-text);
            line-height: 1.6;
        }
        
        .container {
            max-width: 800px;
            margin: 0 auto;
        }
        
        .quiz-header {
            background-color: var(--primary-color);
            color: white;
            padding: 20px;
            position: relative;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .quiz-header::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, rgba(255, 200, 0, 0.3) 0%, rgba(255, 200, 0, 0) 50%);
            z-index: 1;
        }
        
        .quiz-header h1 {
            font-size: 2rem;
            margin: 0;
            position: relative;
            z-index: 2;
        }
        
        .quiz-header img {
            height: 60px;
            border-radius: 8px;
            position: relative;
            z-index: 2;
        }
        
        .language-toggle {
            background-color: var(--light-bg);
            padding: 10px;
            border-radius: 0;
            display: flex;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            border-bottom: 1px solid var(--border-color);
        }
        
        .language-btn {
            background: none;
            border: none;
            padding: 8px 16px;
            margin: 0 5px;
            cursor: pointer;
            border-radius: 20px;
            font-weight: bold;
            transition: all 0.3s ease;
        }
        
        .language-btn.active {
            background-color: var(--primary-color);
            color: white;
        }
        
        .rules-container {
            background-color: white;
            border-radius: 0 0 10px 10px;
            padding: 20px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .rules-section {
            display: none;
        }
        
        .rules-section.active {
            display: block;
            animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        
        .rule-item {
            margin-bottom: 20px;
            border-left: 4px solid var(--primary-color);
            padding-left: 15px;
        }
        
        .rule-item h3 {
            color: var(--primary-color);
            margin-bottom: 8px;
            display: flex;
            align-items: center;
        }
        
        .rule-item h3 i {
            margin-right: 10px;
            font-size: 1.2rem;
        }
        
        .prize-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            background-color: var(--light-bg);
            border-radius: 8px;
            overflow: hidden;
        }
        
        .prize-table th, .prize-table td {
            padding: 12px;
            text-align: center;
            border-bottom: 1px solid var(--border-color);
        }
        
        .prize-table th {
            background-color: var(--primary-color);
            color: white;
        }
        
        .prize-table tr:nth-child(even) {
            background-color: var(--th-color);
        }
        
        .prize-table tr:hover {
            background-color: var(--th-hover-color);
        }
        
        .coin-value {
            color: var(--primary-color);
            font-weight: bold;
        }
        
        .${formData.icons === 'sport' ? 'sport' : 'brain'}-icons {
            display: flex;
            justify-content: center;
            margin: 20px 0;
        }
        
        .${formData.icons === 'sport' ? 'sport' : 'brain'}-icon {
            width: 40px;
            height: 40px;
            margin: 0 10px;
            background-color: var(--th-color);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
        }
        
        .highlight {
            font-weight: bold;
            color: var(--primary-color);
        }
        
        .footer {
            text-align: center;
            margin-top: 20px;
            color: var(--subfont-color);
            font-size: 0.9rem;
        }
               
        .schedule-time {
            font-weight: bold;
            color: var(--primary-color);
            margin-top: 10px;
            display: block;
        }
        
        .countdown {
            background-color: var(--th-color);
            padding: 15px;
            border-radius: 8px;
            text-align: center;
            margin: 15px 0;
        }
        
        .countdown-value {
            font-size: 1.8rem;
            font-weight: bold;
            color: var(--primary-color);
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="quiz-header">
            <h1>${formData.quizTitle}</h1>
        </div>
        
        <div class="language-toggle">
            <button class="language-btn active" id="english-btn">English</button>
            <button class="language-btn" id="hindi-btn">हिंदी</button>
        </div>
        
        <div class="rules-container">
            <!-- English Rules -->
            <div id="english-rules" class="rules-section active">
                              
                <div class="rule-item">
                    <h3>📝 Quiz Description</h3>
                    <p>${formData.quizDescription.english}</p>
                    ${formData.quizType === 'Live' ? `<p>Quiz Type: <span class="highlight">Live</span></p>
                    <p class="schedule-time">Scheduled Time: ${formatDateForDisplay(formData.scheduledDate, formData.scheduledTime)}</p>` : ''}
                </div>
                
                <div class="rule-item">
                    <h3>⏱️ Timing Rules</h3>
                    <p>- Quiz Duration: <span class="highlight">${formData.quizDuration} ${formData.quizType === 'Live' ? 'minutes' : 'hours'}</span>${formData.quizType === 'Daily' ? ` (${formData.quizDuration * 60} minutes)` : ''}</p>
                    <p>- Time per Question: <span class="highlight">${formData.timePerQuestion} seconds</span></p>
                    <p>- Result Announcement: After <span class="highlight">${formData.resultAnnouncement} ${formData.quizType === 'Live' ? 'minutes' : 'hours'}</span>${formData.quizType === 'Daily' ? ` (${formData.resultAnnouncement * 60} minutes)` : ''}</p>
                </div>
                
                <div class="rule-item">
                    <h3>❓ Question Details</h3>
                    <p>- Total Questions: <span class="highlight">${formData.totalQuestions}</span></p>
                    <p>- Difficulty Level Distribution:</p>
                    <ul style="list-style-type: none; margin-left: 20px;">
                        <li>• Easy: ${formData.easyQuestions} question${formData.easyQuestions !== 1 ? 's' : ''}</li>
                        <li>• Medium: ${formData.mediumQuestions} question${formData.mediumQuestions !== 1 ? 's' : ''}</li>
                        <li>• Hard: ${formData.hardQuestions} question${formData.hardQuestions !== 1 ? 's' : ''}</li>
                    </ul>
                </div>
                
                <div class="rule-item">
                    <h3>🏆 Rewards & Prizes</h3>
                    <p>- Total Coin Pool: <span class="highlight">${formData.totalCoinPool.toLocaleString()} coins</span></p>
                    <table class="prize-table">
                        <tr>
                            <th>Rank</th>
                            <th>Prize (Coins)</th>
                        </tr>
                        ${formData.prizes.map(prize => `
                        <tr>
                            <td>${prize.rank}</td>
                            <td class="coin-value">${parseInt(prize.coins).toLocaleString()} coins</td>
                        </tr>`).join('')}
                    </table>
                </div>
                
                <div class="rule-item">
                    <h3>🎮 Gameplay Rules</h3>
                    <p>- Lifelines Available: <span class="highlight">${formData.lifelinesAvailable}</span></p>
                    <p>- Play Limit: You can attempt this quiz <span class="highlight">${formData.playLimit} ${formData.playLimit === 1 ? 'time' : 'times'}${formData.playLimit === 1 ? ' only' : ''}</span></p>
                    <p>- Quiz Type: <span class="highlight">${formData.quizType}</span></p>
                                 </div>
            </div>
            
            <!-- Hindi Rules -->
            <div id="hindi-rules" class="rules-section">
                              
                <div class="rule-item">
                    <h3>📝 क्विज विवरण </h3>
                    <p>${formData.quizDescription.hindi}</p>
                    ${formData.quizType === 'Live' ? `<p>क्विज प्रकार: <span class="highlight">लाइव</span></p>
                    <p class="schedule-time">निर्धारित समय: ${formatDateForDisplay(formData.scheduledDate, formData.scheduledTime, 'hindi')}</p>` : ''}
                </div>
                
                <div class="rule-item">
                    <h3>⏱️ समय नियम</h3>
                    <p>- क्विज अवधि: <span class="highlight">${formData.quizDuration} ${formData.quizType === 'Live' ? 'मिनट' : 'घंटे'}</span>${formData.quizType === 'Daily' ? ` (${formData.quizDuration * 60} मिनट)` : ''}</p>
                    <p>- प्रति प्रश्न समय: <span class="highlight">${formData.timePerQuestion} सेकंड</span></p>
                    <p>- परिणाम घोषणा: <span class="highlight">${formData.resultAnnouncement} ${formData.quizType === 'Live' ? 'मिनट' : 'घंटे'}</span> के बाद${formData.quizType === 'Daily' ? ` (${formData.resultAnnouncement * 60} मिनट)` : ''}</p>
                </div>
                
                <div class="rule-item">
                    <h3>❓ प्रश्न विवरण</h3>
                    <p>- कुल प्रश्न: <span class="highlight">${formData.totalQuestions}</span></p>
                    <p>- कठिनाई स्तर वितरण:</p>
                    <ul style="list-style-type: none; margin-left: 20px;">
                        <li>• आसान: ${formData.easyQuestions} प्रश्न</li>
                        <li>• मध्यम: ${formData.mediumQuestions} प्रश्न</li>
                        <li>• कठिन: ${formData.hardQuestions} प्रश्न</li>
                    </ul>
                </div>
                
                <div class="rule-item">
                    <h3>🏆 पुरस्कार और इनाम</h3>
                    <p>- कुल सिक्का पूल: <span class="highlight">${formData.totalCoinPool.toLocaleString()} सिक्के</span></p>
                    <table class="prize-table">
                        <tr>
                            <th>रैंक</th>
                            <th>पुरस्कार (सिक्के)</th>
                        </tr>
                        ${formData.prizes.map(prize => `
                        <tr>
                            <td>${prize.rank
                .replace("1st Place", "पहला स्थान")
                .replace("2nd Place", "दूसरा स्थान")
                .replace("3rd Place", "तीसरा स्थान")
                .replace("4th Place", "चौथा स्थान")
                .replace("5th Place", "पांचवां स्थान")
                .replace("Place", "स्थान")
                .replace("st", "ला")
                .replace("nd", "रा")
                .replace("rd", "रा")
                .replace("th", "वां")}</td>
                            <td class="coin-value">${parseInt(prize.coins).toLocaleString()} सिक्के</td>
                        </tr>`).join('')}
                    </table>
                </div>
                
                <div class="rule-item">
                    <h3>🎮 गेमप्ले नियम</h3>
                    <p>- उपलब्ध लाइफलाइन: <span class="highlight">${formData.lifelinesAvailable}</span></p>
                    <p>- खेल सीमा: आप इस क्विज को ${formData.playLimit === 1 ? 'केवल ' : ''}<span class="highlight">${formData.playLimit} बार</span> प्रयास कर सकते हैं</p>
                    <p>- क्विज प्रकार: <span class="highlight">${formData.quizType === "Daily" ? "दैनिक" : formData.quizType === "Live" ? "लाइव" : formData.quizType === "Weekly" ? "साप्ताहिक" : formData.quizType === "Monthly" ? "मासिक" : formData.quizType}</span></p>
                                    </div>
            </div>
        </div>
        
        <div class="footer">
            <p>© ${new Date().getFullYear()} ${formData.quizTitle}. All rights reserved.</p>
        </div>
    </div>
    
    <script>
        // Language switcher without inline event handlers
        document.addEventListener('DOMContentLoaded', function() {
            const englishBtn = document.getElementById('english-btn');
            const hindiBtn = document.getElementById('hindi-btn');
            
            englishBtn.addEventListener('click', function() {
                changeLanguage('english', this);
            });
            
            hindiBtn.addEventListener('click', function() {
                changeLanguage('hindi', this);
            });
            
            function changeLanguage(language, clickedButton) {
                // Hide all language sections
                document.querySelectorAll('.rules-section').forEach(section => {
                    section.classList.remove('active');
                });
                
                // Show selected language section
                document.getElementById(language + '-rules').classList.add('active');
                
                // Update button states
                document.querySelectorAll('.language-btn').forEach(btn => {
                    btn.classList.remove('active');
                });
                
                // Add active class to clicked button
                clickedButton.classList.add('active');
            }
            
            ${formData.quizType === 'Live' ? `
            // Format the date
            function formatDate() {
                const scheduledDate = new Date("${formData.scheduledDate}T${formData.scheduledTime}:00.000Z");
                
                const options = {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                };
                
                const formattedDate = scheduledDate.toLocaleDateString('en-US', options);
                document.querySelector('#english-rules .schedule-time').textContent = "Scheduled Time: " + formattedDate;
                
                // Hindi date format (simplified)
                const hindiMonths = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
                const hindiDay = scheduledDate.getDate();
                const hindiMonth = hindiMonths[scheduledDate.getMonth()];
                const hindiYear = scheduledDate.getFullYear();
                const hindiHour = scheduledDate.getHours();
                const hindiMinute = scheduledDate.getMinutes();
                
                const hindiTime = \`\${hindiHour}:\${hindiMinute < 10 ? '0' + hindiMinute : hindiMinute}\`;
                const hindiDate = \`\${hindiDay} \${hindiMonth}, \${hindiYear} शाम \${hindiTime} बजे\`;
                
                document.querySelector('#hindi-rules .schedule-time').textContent = "निर्धारित समय: " + hindiDate;
            }
            
            // Call the date formatting function
            formatDate();` : ''}
        });
    </script>
</body>
</html>`;

        return html;
    };

    // Handle HTML code changes
    const handleCodeChange = (e) => {
        setHtmlCode(e.target.value);
    };

    // Handle saving HTML changes
    const handleSaveHtml = () => {
        setEditMode(false);
        // Set the flag to use edited HTML
        setUseEditedHtml(true);
    };

    // Handle reset HTML to original
    const handleResetHtml = () => {
        setHtmlCode(originalHtmlCode);
    };

    // Function to handle HTML download
    const downloadHTML = () => {
        // Use the current HTML (edited or generated)
        const html = useEditedHtml ? htmlCode : generateHTML();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${formData.quizTitle.replace(/\s+/g, '-').toLowerCase()}-quiz-template.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Call the parent callback if provided
        if (typeof onDownload === 'function') {
            onDownload(html);
        }
    };

    // Color Picker Component
    const ColorPicker = () => (
        <div className="mt-4 p-6 bg-white rounded-lg border border-gray-300 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-800">Color Customization</h4>
                <button
                    onClick={() => setShowColorPicker(false)}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Color
                    </label>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full border border-gray-300" style={{ backgroundColor: formData.primaryColor }}></div>
                        <input
                            type="text"
                            value={formData.primaryColor}
                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                        <input
                            type="color"
                            value={formData.primaryColor}
                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                            className="h-8 w-8 cursor-pointer"
                        />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Used for headings, badges, and highlights</p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Secondary Color
                    </label>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full border border-gray-300" style={{ backgroundColor: formData.secondaryColor }}></div>
                        <input
                            type="text"
                            value={formData.secondaryColor}
                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                        <input
                            type="color"
                            value={formData.secondaryColor}
                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                            className="h-8 w-8 cursor-pointer"
                        />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Used for gradients and accents</p>
                </div>
            </div>

            <div className="mt-6">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Color Presets</h5>
                <div className="flex flex-wrap gap-2">
                    {[
                        { name: 'Blue/Yellow', primary: '#6849FF', secondary: '#FFC800' },
                        { name: 'Red/Orange', primary: '#E63946', secondary: '#F4A261' },
                        { name: 'Green/Teal', primary: '#2A9D8F', secondary: '#E9C46A' },
                        { name: 'Purple/Pink', primary: '#9D4EDD', secondary: '#FF758F' },
                        { name: 'Dark/Light', primary: '#2B2D42', secondary: '#8D99AE' },
                        { name: 'Ocean', primary: '#023E8A', secondary: '#0077B6' }
                    ].map((preset, index) => (
                        <button
                            key={index}
                            onClick={() => {
                                setFormData({
                                    ...formData,
                                    primaryColor: preset.primary,
                                    secondaryColor: preset.secondary
                                });
                                // Reset to use generated HTML
                                setUseEditedHtml(false);
                            }}
                            className="px-3 py-1 text-xs rounded-full border flex items-center gap-1"
                            style={{
                                borderColor: preset.primary,
                                backgroundColor: 'white'
                            }}
                        >
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.primary }}></span>
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.secondary }}></span>
                            {preset.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    // Live Preview Component with a ref for accessing the iframe
    const LivePreviewComponent = () => {
        const iframeRef = useRef(null);

        useEffect(() => {
            if (iframeRef.current) {
                const iframe = iframeRef.current;
                const doc = iframe.contentDocument || iframe.contentWindow.document;

                // Clear the document
                doc.open();
                // Write the current HTML (either edited or generated)
                doc.write(useEditedHtml ? htmlCode : generateHTML());
                doc.close();

                // Add an event listener to handle language changes after the iframe loads
                iframe.onload = () => {
                    const iframeWindow = iframe.contentWindow;
                    if (iframeWindow) {
                        // Set the language through DOM manipulation after the iframe has loaded
                        const hindiBtn = iframeWindow.document.getElementById('hindi-btn');
                        const englishBtn = iframeWindow.document.getElementById('english-btn');

                        if (previewLanguage === 'hindi' && hindiBtn) {
                            // Manually trigger the language change
                            iframeWindow.document.querySelectorAll('.rules-section').forEach(section => {
                                section.classList.remove('active');
                            });
                            iframeWindow.document.getElementById('hindi-rules').classList.add('active');
                            iframeWindow.document.querySelectorAll('.language-btn').forEach(btn => {
                                btn.classList.remove('active');
                            });
                            hindiBtn.classList.add('active');
                        } else if (previewLanguage === 'english' && englishBtn) {
                            // Ensure English is active (it should be by default)
                            iframeWindow.document.querySelectorAll('.rules-section').forEach(section => {
                                section.classList.remove('active');
                            });
                            iframeWindow.document.getElementById('english-rules').classList.add('active');
                            iframeWindow.document.querySelectorAll('.language-btn').forEach(btn => {
                                btn.classList.remove('active');
                            });
                            englishBtn.classList.add('active');
                        }
                    }
                };
            }
        }, [previewLanguage, useEditedHtml, htmlCode, formData]);

        return (
            <div className="bg-gray-50 p-2 rounded-lg shadow-inner max-w-4xl mx-auto">
                <div className="flex justify-center mb-2">
                    <button
                        onClick={() => setPreviewLanguage('english')}
                        className={`px-3 py-1 text-sm rounded-l-lg ${previewLanguage === 'english'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700'
                            }`}
                    >
                        English
                    </button>
                    <button
                        onClick={() => setPreviewLanguage('hindi')}
                        className={`px-3 py-1 text-sm rounded-r-lg ${previewLanguage === 'hindi'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700'
                            }`}
                    >
                        हिंदी
                    </button>
                </div>
                <iframe
                    ref={iframeRef}
                    className="w-full h-[600px] border-0"
                    title="Quiz Rules Preview"
                />
            </div>
        );
    };

    // Icons (using SVG instead of react-icons/fa for compatibility)
    const DownloadIcon = () => (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M433.941 129.941l-83.882-83.882A48 48 0 0 0 316.118 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V163.882a48 48 0 0 0-14.059-33.941zM272 80v80H144V80h128zm122 352c0 8.822-7.178 16-16 16H48c-8.822 0-16-7.178-16-16V80c0-8.822 7.178-16 16-16h79.017v80H44c-6.627 0-12 5.373-12 12v16c0 6.627 5.373 12 12 12h384c6.627 0 12-5.373 12-12v-16c0-6.627-5.373-12-12-12h-83.017V64H316.118a16 16 0 0 0-11.314 4.686L208 168.686V72a8 8 0 0 0-8-8h-16a8 8 0 0 0-8 8v120.686L82.627 100.628A8 8 0 0 0 77.255 99H68a8 8 0 0 0-5.657 13.657L160 210.343V280a8 8 0 0 0 8 8h16a8 8 0 0 0 8-8v-69.657L288.343 114c3.135-3.135 3.135-8.223 0-11.358a8.002 8.002 0 0 0-11.314 0L208 171.313v-98.627L305.941 168H320a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8h-39.029V80H320c8.822 0 16 7.178 16 16v185.014l-96-96L145.941 279.986 128 261.941V280a8 8 0 0 0 8 8h16a8 8 0 0 0 8-8v-26.627l94.627 94.63a7.985 7.985 0 0 0 5.374 2.059h8.18a7.99 7.99 0 0 0 5.656-2.343L368 253.255v178.743z" /></svg>
    );

    const EyeIcon = () => (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M288 144a110.94 110.94 0 0 0-31.24 5 55.4 55.4 0 0 1 7.24 27 56 56 0 0 1-56 56 55.4 55.4 0 0 1-27-7.24A111.71 111.71 0 1 0 288 144zm284.52 97.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400c-98.65 0-189.09-55-237.93-144C98.91 167 189.34 112 288 112s189.09 55 237.93 144C477.1 345 386.66 400 288 400z" /></svg>
    );

    const EyeSlashIcon = () => (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="currentColor" d="M320 400c-75.85 0-137.25-58.71-142.9-133.11L72.2 185.82c-13.79 17.3-26.48 35.59-36.72 55.59a32.35 32.35 0 0 0 0 29.19C89.71 376.41 197.07 448 320 448c26.91 0 52.87-4 77.89-10.46L346 397.39a144.13 144.13 0 0 1-26 2.61zm313.82 58.1l-110.55-85.44a331.25 331.25 0 0 0 81.25-102.07 32.35 32.35 0 0 0 0-29.19C550.29 135.59 442.93 64 320 64a308.15 308.15 0 0 0-147.32 37.7L45.46 3.37A16 16 0 0 0 23 6.18L3.37 31.45A16 16 0 0 0 6.18 53.9l588.36 454.73a16 16 0 0 0 22.46-2.81l19.64-25.27a16 16 0 0 0-2.82-22.45zm-183.72-142l-39.3-30.38A94.75 94.75 0 0 0 416 256a94.76 94.76 0 0 0-121.31-92.21A47.65 47.65 0 0 1 304 192a46.64 46.64 0 0 1-1.54 10l-73.61-56.89A142.31 142.31 0 0 1 320 112a143.92 143.92 0 0 1 144 144c0 21.63-5.29 41.79-13.9 60.11z" /></svg>
    );

    const CodeIcon = () => (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="currentColor" d="M278.9 511.5l-61-17.7c-6.4-1.8-10-8.5-8.2-14.9L346.2 8.7c1.8-6.4 8.5-10 14.9-8.2l61 17.7c6.4 1.8 10 8.5 8.2 14.9L293.8 503.3c-1.9 6.4-8.5 10.1-14.9 8.2zm-114-112.2l43.5-46.4c4.6-4.9 4.3-12.7-.8-17.2L117 256l90.6-79.7c5.1-4.5 5.5-12.3.8-17.2l-43.5-46.4c-4.5-4.8-12.1-5.1-17-.5L3.8 247.2c-5.1 4.7-5.1 12.8 0 17.5l144.1 135.1c4.9 4.6 12.5 4.4 17-.5zm327.2.6l144.1-135.1c5.1-4.7 5.1-12.8 0-17.5L492.1 112.1c-4.8-4.5-12.4-4.3-17 .5L431.6 159c-4.6 4.9-4.3 12.7.8 17.2L523 256l-90.6 79.7c-5.1 4.5-5.5 12.3-.8 17.2l43.5 46.4c4.5 4.9 12.1 5.1 17 .6z" /></svg>
    );

    const ExclamationTriangleIcon = () => (
        <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028zM288 354c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z" /></svg>
    );

    const InfoCircleIcon = () => (
        <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z" /></svg>
    );

    const PaletteIcon = () => (
        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M204.3 5C104.9 24.4 24.8 104.3 5.2 203.4c-37 187 131.7 326.4 258.8 306.7 41.2-6.4 61.4-54.6 42.5-91.7-23.1-45.4 9.9-98.4 60.9-98.4h79.7c35.8 0 64.8-29.6 64.9-65.3C511.5 97.1 368.1-26.9 204.3 5zM96 320c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm32-128c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm128-64c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm128 64c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32z" /></svg>
    );

    const EditIcon = () => (
        <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.3 15.2-40 0-55.2zM384 346.2V448H64V128h229.8c3.2 0 6.2-1.3 8.5-3.5l40-40c7.6-7.6 2.2-20.5-8.5-20.5H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V306.2c0-10.7-12.9-16-20.5-8.5l-40 40c-2.2 2.3-3.5 5.3-3.5 8.5z" /></svg>
    );

    const SaveIcon = () => (
        <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M433.941 129.941l-83.882-83.882A48 48 0 0 0 316.118 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V163.882a48 48 0 0 0-14.059-33.941zM224 416c-35.346 0-64-28.654-64-64 0-35.346 28.654-64 64-64s64 28.654 64 64c0 35.346-28.654 64-64 64zm96-304.52V212c0 6.627-5.373 12-12 12H76c-6.627 0-12-5.373-12-12V108c0-6.627 5.373-12 12-12h228.52c3.183 0 6.235 1.264 8.485 3.515l3.48 3.48A11.996 11.996 0 0 1 320 111.48z" /></svg>
    );

    const UndoIcon = () => (
        <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M255.545 8c-66.269.119-126.438 26.233-170.86 68.685L48.971 40.971C33.851 25.851 8 36.559 8 57.941V192c0 13.255 10.745 24 24 24h134.059c21.382 0 32.09-25.851 16.971-40.971l-41.75-41.75c30.864-28.899 70.801-44.907 113.23-45.273 92.398-.798 170.283 73.977 169.484 169.442C423.236 348.009 349.816 424 256 424c-41.127 0-79.997-14.678-110.63-41.556-4.743-4.161-11.906-3.908-16.368.553L89.34 422.659c-4.872 4.872-4.631 12.815.482 17.433C133.798 479.813 192.074 504 256 504c136.966 0 247.999-111.033 248-247.998C504.001 119.193 392.354 7.755 255.545 8z" /></svg>
    );

    const LanguageIcon = () => (
        <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="currentColor" d="M152.1 236.2c-3.5-12.1-7.8-33.2-7.8-33.2h-.5s-4.3 21.1-7.8 33.2l-11.1 37.5H163zM616 96H336v320h280c13.3 0 24-10.7 24-24V120c0-13.3-10.7-24-24-24zm-24 120c0 6.6-5.4 12-12 12h-11.4c-6.9 23.6-21.7 47.4-42.7 69.9 8.4 6.4 17.1 12.5 26.1 18 5.5 3.4 7.3 10.5 4.1 16.2l-7.9 13.9c-3.4 5.9-10.9 7.8-16.7 4.3-12.6-7.8-24.5-16.1-35.4-24.9-10.9 8.7-22.7 17.1-35.4 24.9-5.8 3.5-13.3 1.6-16.7-4.3l-7.9-13.9c-3.2-5.6-1.4-12.8 4.2-16.2 9.3-5.7 18-11.7 26.1-18-7.9-8.4-14.9-17-21-25.7-4-5.7-2.2-13.6 3.7-17.1l6.5-3.9 7.3-4.3c5.4-3.2 12.4-1.7 16 3.4 5 7 10.8 14 17.4 20.9 13.5-14.2 23.8-28.9 30-43.2H412c-6.6 0-12-5.4-12-12v-16c0-6.6 5.4-12 12-12h64v-16c0-6.6 5.4-12 12-12h16c6.6 0 12 5.4 12 12v16h64c6.6 0 12 5.4 12 12zM0 120v272c0 13.3 10.7 24 24 24h280V96H24c-13.3 0-24 10.7-24 24zm58.9 216.1L116.4 167c1.7-4.9 6.2-8.1 11.4-8.1h32.5c5.1 0 9.7 3.3 11.4 8.1l57.5 169.1c2.6 7.8-3.1 15.9-11.4 15.9h-22.9a12 12 0 0 1-11.5-8.6l-9.4-31.9h-60.2l-9.1 31.8c-1.5 5.1-6.2 8.7-11.5 8.7H70.3c-8.2 0-14-8.1-11.4-15.9z" /></svg>
    );

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                        <InfoCircleIcon />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Quiz Rules Generator</h3>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all flex items-center gap-2"
                    >
                        <PaletteIcon />
                        Customize Colors
                    </button>
                    <button
                        onClick={() => setPreview(!preview)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all flex items-center gap-2"
                    >
                        {preview ? <EyeSlashIcon /> : <EyeIcon />}
                        {preview ? "Hide Preview" : "Show Preview"}
                    </button>
                    <button
                        onClick={downloadHTML}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all flex items-center gap-2"
                    >
                        <DownloadIcon />
                        Download HTML
                    </button>
                </div>
            </div>

            {showColorPicker && <ColorPicker />}

            {!preview && !showColorPicker && (
                <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-100">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2">
                        <FaExclamationTriangle className="w-5 h-5" />
                        <p className="font-medium">Rules are automatically generated based on quiz settings</p>
                    </div>
                    <p className="text-indigo-700 text-sm">
                        This component will create an HTML file with both English and Hindi rules for your quiz.
                        You can customize colors, preview, edit the HTML code directly, and download the file.
                    </p>
                </div>
            )}

            {preview && (
                <div className="mb-4">
                    <div className="border-b border-gray-200 flex rounded-t overflow-hidden">
                        <button
                            onClick={() => setPreviewTab('visual')}
                            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 ${previewTab === 'visual'
                                ? 'bg-indigo-50 text-indigo-700 font-bold'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FaEye className="w-4 h-4" />
                            Visual Preview
                        </button>
                        <button
                            onClick={() => setPreviewTab('code')}
                            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 ${previewTab === 'code'
                                ? 'bg-indigo-50 text-indigo-700 font-bold'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FaCode className="w-4 h-4" />
                            HTML Code
                        </button>
                    </div>

                    {previewTab === 'visual' ? (
                        <div className="mt-4">
                            <LivePreviewComponent />
                        </div>
                    ) : (
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="text-lg font-bold text-gray-700">HTML Code</h2>
                                <div className="flex gap-2">
                                    {editMode ? (
                                        <>
                                            <button
                                                onClick={handleSaveHtml}
                                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-all text-sm flex items-center gap-1"
                                            >
                                                <FaSave className="w-3 h-3" />
                                                Save Changes
                                            </button>
                                            <button
                                                onClick={handleResetHtml}
                                                className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-all text-sm flex items-center gap-1"
                                            >
                                                <FaUndo className="w-3 h-3" />
                                                Reset
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setEditMode(true)}
                                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all text-sm flex items-center gap-1"
                                        >
                                            <FaEdit className="w-3 h-3" />
                                            Edit HTML
                                        </button>
                                    )}
                                </div>
                            </div>
                            {editMode ? (
                                <div className="relative">
                                    <textarea
                                        value={htmlCode}
                                        onChange={handleCodeChange}
                                        className="w-full h-[500px] bg-gray-800 text-gray-200 p-4 rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            ) : (
                                <div className="bg-gray-800 text-gray-200 p-4 rounded-lg overflow-auto max-h-[500px] text-sm">
                                    <pre className="font-mono">
                                        {useEditedHtml ? htmlCode : generateHTML()}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!preview && !showColorPicker && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                        <FaLanguage className="w-5 h-5 text-indigo-600" />
                        <h4 className="font-semibold text-gray-700">Features</h4>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-2">
                        <li className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span>Bilingual support with English and Hindi language toggle</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span>Customizable color scheme with preset templates</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span>Direct HTML code editing for advanced customization</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span>Mobile-responsive design with clean, modern UI</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            <span>Downloadable HTML file that works offline</span>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default QuizRulesGenerator;